import { useEffect, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useFieldError, useT } from '@kmgestao/i18n';
import { Button, Input, Label, toast } from '@kmgestao/ui';

import {
  clinicProfileSchema,
  normalizeClinicProfileInput,
  useUpdateClinicProfile,
  type ClinicProfileInput,
  type ClinicProfileRow,
} from '../../../features/clinic-profile/index.js';
import {
  formatCep,
  formatPhoneBR,
  maskCnpjProgressive,
  normalizeCep,
  toTitleCase,
  useCepLookup,
} from '../../../features/leads/index.js';

export interface ClinicProfileFormProps {
  clinicId: string;
  /** Dados atuais (vem do `useClinicProfile`). Form usa pra `defaultValues`. */
  data: ClinicProfileRow;
}

/**
 * Form principal da página `/perfil`. Inline (não é Dialog) — estamos
 * em uma página dedicada e o form ocupa o espaço natural do
 * `PageSectionCard`.
 *
 * **Campos read-only**: razão social e CNPJ aparecem como `<Input
 * disabled>` pra deixar visualmente claro que são intocáveis. Não
 * passam pelo schema/mutation.
 *
 * **Autofill por CEP** (via `useCepLookup` reusado de `features/leads/`):
 * quando user digita CEP novo (8 dígitos), preenche street/district/
 * city/uf — mas SÓ se o CEP for diferente do que estava salvo (em
 * edição, owner pode ter customizado endereço; não queremos
 * sobrescrever sem motivo). Mesmo padrão `formCepInitial.current` do
 * `LeadFormFields`.
 *
 * **Padronização visual no submit**: aplica `normalizeClinicProfileInput`
 * (Title Case em nomes, UPPERCASE em UF, formato canônico em
 * phone/CEP) antes de chamar a mutation. Decisão de produto — manter
 * UM padrão visual no app.
 */
export function ClinicProfileForm({ clinicId, data }: ClinicProfileFormProps) {
  const { t } = useT('clinics');
  const { t: tCommon } = useT('common');
  const tError = useFieldError();
  const updateMutation = useUpdateClinicProfile();

  const initialAddress = data.address ?? {};
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<ClinicProfileInput>({
    resolver: zodResolver(clinicProfileSchema),
    defaultValues: {
      trade_name: data.trade_name ?? '',
      email: data.email ?? '',
      phone: data.phone ? formatPhoneBR(data.phone) : '',
      address: {
        cep: initialAddress.cep ? formatCep(initialAddress.cep) : '',
        street: initialAddress.street ?? '',
        number: initialAddress.number ?? '',
        complement: initialAddress.complement ?? '',
        district: initialAddress.district ?? '',
        city: initialAddress.city ?? '',
        uf: initialAddress.uf ?? '',
      },
    },
  });

  // Autofill por CEP — espelha LeadFormFields. CEP inicial (do banco)
  // não dispara overwrite; apenas mudanças explícitas do user.
  const cepRaw = watch('address.cep');
  const cepDigits = normalizeCep(cepRaw);
  const cepQuery = useCepLookup(cepDigits);

  const formCepInitial = useRef<string | undefined>(undefined);
  if (formCepInitial.current === undefined) {
    formCepInitial.current = normalizeCep(getValues('address.cep'));
  }

  useEffect(() => {
    if (!cepQuery.data) return;
    if (cepDigits === formCepInitial.current) return;
    const d = cepQuery.data;
    if (d.street) {
      setValue('address.street', toTitleCase(d.street), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    if (d.neighborhood) {
      setValue('address.district', toTitleCase(d.neighborhood), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
    if (d.city) {
      setValue('address.city', toTitleCase(d.city), { shouldValidate: true, shouldDirty: true });
    }
    if (d.state) {
      setValue('address.uf', d.state.toUpperCase().slice(0, 2), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [cepQuery.data, cepDigits, setValue]);

  const onSubmit = handleSubmit(async (rawValues) => {
    if (!isDirty) {
      toast.info(t('profile.feedback.noChanges'));
      return;
    }
    const normalized = normalizeClinicProfileInput(rawValues);
    try {
      await updateMutation.mutateAsync({ clinicId, values: normalized });
      toast.success(t('profile.feedback.saveSuccess'));
      // Reset com os valores normalizados pra refletir formatação
      // canônica nos campos visíveis (e marcar form como pristine).
      reset({
        trade_name: normalized.trade_name ?? '',
        email: normalized.email ?? '',
        phone: normalized.phone ?? '',
        address: {
          cep: normalized.address.cep ?? '',
          street: normalized.address.street ?? '',
          number: normalized.address.number ?? '',
          complement: normalized.address.complement ?? '',
          district: normalized.address.district ?? '',
          city: normalized.address.city ?? '',
          uf: normalized.address.uf ?? '',
        },
      });
    } catch (err) {
      toast.error(t('profile.feedback.saveError'), {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  });

  const tradeNameError = tError(errors.trade_name);
  const emailError = tError(errors.email);
  const phoneError = tError(errors.phone);
  const cepError = tError(errors.address?.cep);
  const ufError = tError(errors.address?.uf);

  const submitDisabled = isSubmitting || updateMutation.isPending || !isDirty;

  return (
    <form onSubmit={onSubmit} className="space-y-8" noValidate>
      {/* ── Seção: Identificação ───────────────────────────────────── */}
      <section className="space-y-4">
        <header className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">
            {t('profile.sections.identity.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('profile.sections.identity.description')}
          </p>
        </header>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="profile-legal-name">{t('profile.fields.legalName.label')}</Label>
            <Input
              id="profile-legal-name"
              type="text"
              value={data.legal_name}
              readOnly
              disabled
              autoComplete="off"
            />
            <p className="text-sm text-muted-foreground">{t('profile.fields.legalName.hint')}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-cnpj">{t('profile.fields.cnpj.label')}</Label>
            <Input
              id="profile-cnpj"
              type="text"
              value={data.cnpj ? maskCnpjProgressive(data.cnpj) : ''}
              readOnly
              disabled
              autoComplete="off"
              inputMode="numeric"
            />
            <p className="text-sm text-muted-foreground">{t('profile.fields.cnpj.hint')}</p>
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="profile-trade-name">{t('profile.fields.tradeName.label')}</Label>
            <Input
              id="profile-trade-name"
              type="text"
              autoComplete="organization"
              placeholder={t('profile.fields.tradeName.placeholder')}
              hasError={!!errors.trade_name}
              {...register('trade_name')}
            />
            <p className="text-sm text-muted-foreground">
              {tradeNameError ?? t('profile.fields.tradeName.hint')}
            </p>
          </div>
        </div>
      </section>

      {/* ── Seção: Contato ──────────────────────────────────────────── */}
      <section className="space-y-4">
        <header className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">
            {t('profile.sections.contact.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('profile.sections.contact.description')}
          </p>
        </header>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="profile-email">{t('profile.fields.email.label')}</Label>
            <Input
              id="profile-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder={t('profile.fields.email.placeholder')}
              hasError={!!errors.email}
              {...register('email')}
            />
            <p className="text-sm text-muted-foreground">
              {emailError ?? t('profile.fields.email.hint')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-phone">{t('profile.fields.phone.label')}</Label>
            <Input
              id="profile-phone"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              placeholder={t('profile.fields.phone.placeholder')}
              hasError={!!errors.phone}
              maxLength={16}
              {...register('phone')}
              onChange={(e) => {
                setValue('phone', formatPhoneBR(e.target.value), {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
            />
            <p className="text-sm text-muted-foreground">
              {phoneError ?? t('profile.fields.phone.hint')}
            </p>
          </div>
        </div>
      </section>

      {/* ── Seção: Endereço ─────────────────────────────────────────── */}
      <section className="space-y-4">
        <header className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">
            {t('profile.sections.address.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('profile.sections.address.description')}
          </p>
        </header>

        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor="profile-cep">{t('profile.fields.address.cep.label')}</Label>
            <Input
              id="profile-cep"
              type="text"
              autoComplete="postal-code"
              inputMode="numeric"
              maxLength={9}
              placeholder={t('profile.fields.address.cep.placeholder')}
              hasError={!!errors.address?.cep}
              {...register('address.cep')}
              onChange={(e) => {
                setValue('address.cep', formatCep(e.target.value), {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
            />
            <p className="text-sm text-muted-foreground">
              {cepError ??
                (cepQuery.isLoading
                  ? t('profile.fields.address.cep.lookingUp')
                  : cepQuery.isError
                    ? t('profile.fields.address.cep.notFound')
                    : t('profile.fields.address.cep.hint'))}
            </p>
          </div>

          <div className="space-y-2 sm:col-span-2 lg:col-span-3">
            <Label htmlFor="profile-street">{t('profile.fields.address.street.label')}</Label>
            <Input
              id="profile-street"
              type="text"
              autoComplete="address-line1"
              placeholder={t('profile.fields.address.street.placeholder')}
              {...register('address.street')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-number">{t('profile.fields.address.number.label')}</Label>
            <Input
              id="profile-number"
              type="text"
              autoComplete="address-line2"
              inputMode="numeric"
              placeholder={t('profile.fields.address.number.placeholder')}
              {...register('address.number')}
            />
          </div>

          <div className="space-y-2 sm:col-span-2 lg:col-span-3">
            <Label htmlFor="profile-complement">
              {t('profile.fields.address.complement.label')}
            </Label>
            <Input
              id="profile-complement"
              type="text"
              autoComplete="address-line2"
              placeholder={t('profile.fields.address.complement.placeholder')}
              {...register('address.complement')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-district">{t('profile.fields.address.district.label')}</Label>
            <Input
              id="profile-district"
              type="text"
              autoComplete="address-level3"
              placeholder={t('profile.fields.address.district.placeholder')}
              {...register('address.district')}
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="profile-city">{t('profile.fields.address.city.label')}</Label>
            <Input
              id="profile-city"
              type="text"
              autoComplete="address-level2"
              placeholder={t('profile.fields.address.city.placeholder')}
              {...register('address.city')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-uf">{t('profile.fields.address.uf.label')}</Label>
            <Input
              id="profile-uf"
              type="text"
              autoComplete="address-level1"
              maxLength={2}
              placeholder={t('profile.fields.address.uf.placeholder')}
              hasError={!!errors.address?.uf}
              {...register('address.uf')}
              onChange={(e) => {
                setValue('address.uf', e.target.value.toUpperCase().slice(0, 2), {
                  shouldValidate: true,
                  shouldDirty: true,
                });
              }}
            />
            {ufError ? (
              <p className="text-sm text-destructive" role="alert">
                {ufError}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      {/* ── Ações ───────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 border-t border-border pt-4">
        <Button type="submit" size="lg" disabled={submitDisabled}>
          {submitDisabled && (isSubmitting || updateMutation.isPending)
            ? tCommon('status.saving')
            : t('profile.actions.save')}
        </Button>
        <Button
          type="button"
          size="lg"
          variant="outline"
          disabled={!isDirty || isSubmitting || updateMutation.isPending}
          onClick={() => reset()}
        >
          {t('profile.actions.discard')}
        </Button>
      </div>
    </form>
  );
}
